import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  Flex,
  Grid,
  Group,
  Loader,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
  Center,
  PasswordInput,
} from '@mantine/core';
import {
  IconKey,
  IconShieldLock,
  IconUser,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';
import api from '../api/axiosConfig';
import ProfileLeaveCard from '../components/ProfileLeaveCard';


dayjs.locale('tr');

function InfoCard({ title, children }) {
  return (
    <Paper
      withBorder
      radius="xl"
      p="md"
      style={{
        background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
        boxShadow: '0 10px 30px rgba(15,23,42,0.05)',
      }}
    >
      <Stack gap="md">
        <div>
          <Title order={4} fw={800}>
            {title}
          </Title>
        </div>
        <Divider />
        {children}
      </Stack>
    </Paper>
  );
}

function InfoItem({ label, value }) {
  return (
    <div>
      <Text size="xs" c="dimmed" fw={600}>
        {label}
      </Text>
      <Text size="sm" fw={600}>
        {value || '-'}
      </Text>
    </div>
  );
}

export default function ProfilePage() {
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);

  const [supportList, setSupportList] = useState([]);

  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    new_password_repeat: '',
  });
  const [passwordSubmitting, setPasswordSubmitting] = useState(false);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const [profileRes, supportRes] = await Promise.all([
        api.get('/profile/me'),
        api.get('/profile/support-requests'),
      ]);

      setProfile(profileRes.data);
      setSupportList(supportRes.data || []);
    } catch (err) {
      notifications.show({
        title: 'Hata',
        message: 'Profil bilgileri alınamadı.',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  const handleChangePassword = async () => {
    if (!passwordForm.current_password || !passwordForm.new_password) {
      notifications.show({
        title: 'Uyarı',
        message: 'Şifre alanlarını doldurunuz.',
        color: 'yellow',
      });
      return;
    }

    if (passwordForm.new_password.length < 6) {
      notifications.show({
        title: 'Uyarı',
        message: 'Yeni şifre en az 6 karakter olmalıdır.',
        color: 'yellow',
      });
      return;
    }

    if (passwordForm.new_password !== passwordForm.new_password_repeat) {
      notifications.show({
        title: 'Uyarı',
        message: 'Yeni şifre tekrar alanı eşleşmiyor.',
        color: 'yellow',
      });
      return;
    }

    setPasswordSubmitting(true);
    try {
      await api.put('/profile/change-password', {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password,
      });

      notifications.show({
        title: 'Başarılı',
        message: 'Şifreniz güncellendi.',
        color: 'teal',
      });

      setPasswordForm({
        current_password: '',
        new_password: '',
        new_password_repeat: '',
      });
    } catch (err) {
      notifications.show({
        title: 'Hata',
        message: err.response?.data?.detail || 'Şifre güncellenemedi.',
        color: 'red',
      });
    } finally {
      setPasswordSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Center py={80}>
        <Stack align="center">
          <Loader />
          <Text c="dimmed">Profil yükleniyor...</Text>
        </Stack>
      </Center>
    );
  }

  const employee = profile?.employee_info;
  const account = profile?.account_info;
  const unit = profile?.unit_info;

  return (
    <Stack gap="lg" style={{ minHeight: '70vh' }}>
      {/* HERO */}
      <Paper
        radius="xl"
        p={{ base: 'lg', md: 'xl' }}
        style={{
          position: 'relative',
          overflow: 'hidden',
          background:
            'linear-gradient(135deg, rgba(15,23,42,0.98) 0%, rgba(30,41,59,0.96) 40%, rgba(37,99,235,0.88) 100%)',
          border: '1px solid rgba(255,255,255,0.08)',
          boxShadow: '0 18px 45px rgba(15,23,42,0.18)',
        }}
      >
        <Box
          style={{
            position: 'absolute',
            top: -70,
            right: -70,
            width: 220,
            height: 220,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.08)',
          }}
        />
        <Box
          style={{
            position: 'absolute',
            bottom: -60,
            left: -60,
            width: 180,
            height: 180,
            borderRadius: '50%',
            background: 'rgba(96,165,250,0.18)',
          }}
        />

        <Flex
          justify="space-between"
          align={{ base: 'flex-start', md: 'center' }}
          wrap="wrap"
          gap="md"
          style={{ position: 'relative', zIndex: 1 }}
        >
          <Group align="flex-start" wrap="nowrap">
            <ThemeIcon
              size={54}
              radius="xl"
              variant="white"
              color="rgba(255,255,255,0.12)"
            >
              <IconUser size={28} color="white" />
            </ThemeIcon>

            <div>
              <Text fw={700} size="sm" c="rgba(255,255,255,0.75)">
                Kurumsal Portal
              </Text>
              <Title order={2} c="white" fw={900}>
                Profilim
              </Title>
              <Text
                size="sm"
                maw={760}
                style={{ color: 'rgba(255,255,255,0.78)', lineHeight: 1.7 }}
              >
                Kişisel personel kaydınızı, sistem hesabınızı, kurumsal imzanızı,
                bağlı olduğunuz birim ve mail gruplarını görüntüleyin. Bilgi
                güncelleme veya destek ihtiyaçlarınız için talep oluşturabilirsiniz.
              </Text>
            </div>
          </Group>

          <Paper
            radius="xl"
            p="md"
            style={{
              minWidth: 260,
              background: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.12)',
              backdropFilter: 'blur(12px)',
            }}
          >
            <Group wrap="nowrap">
              <ThemeIcon
                size={42}
                radius="xl"
                variant="white"
                color="rgba(255,255,255,0.14)"
              >
                <IconShieldLock size={20} color="white" />
              </ThemeIcon>

              <div>
                <Text size="xs" fw={700} c="rgba(255,255,255,0.70)">
                  Hesap Durumu
                </Text>
                <Text fw={800} c="white">
                  {account?.is_active ? 'Aktif Kullanıcı' : 'Pasif Kullanıcı'}
                </Text>
              </div>
            </Group>
          </Paper>
        </Flex>
      </Paper>

      <Paper
        withBorder
        radius="xl"
        p="md"
        style={{
          background: 'var(--mantine-color-yellow-0)',
          borderColor: 'var(--mantine-color-yellow-2)',
        }}
      >
        <Text size="sm">
          Kişisel ve organizasyonel bilgileriniz sistem yöneticileri tarafından yönetilmektedir.
        </Text>
      </Paper>

      <Grid>
        <Grid.Col span={{ base: 12, lg: 6 }}>
          <InfoCard title="Kendi Personel Bilgilerim">
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
              <InfoItem label="Ad Soyad" value={employee?.full_name} />
              <InfoItem label="Personel Tipi" value={employee?.employee_type === 'OFFICE' ? 'Merkez Ofis' : employee?.employee_type === 'SITE' ? 'Şantiye' : '-'} />
              <InfoItem label="Pozisyon" value={employee?.position} />
              <InfoItem label="Departman" value={employee?.department} />
              <InfoItem label="Şantiye" value={employee?.site_name} />
              <InfoItem label="Telefon" value={employee?.phone} />
              <InfoItem label="E-posta" value={employee?.email} />
              <InfoItem label="Durum" value={employee?.is_active ? 'Aktif' : 'Pasif'} />
            </SimpleGrid>
          </InfoCard>
        </Grid.Col>

        <Grid.Col span={{ base: 12, lg: 6 }}>
          <InfoCard title="Hesap Bilgilerim">
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
              <InfoItem label="Kullanıcı Adı" value={account?.username} />
              <InfoItem label="Rol" value={account?.role} />
              <InfoItem label="Ad Soyad" value={employee?.full_name} />
              <InfoItem label="Hesap Durumu" value={account?.is_active ? 'Aktif' : 'Pasif'} />
              <InfoItem label="E-posta" value={employee?.email} />
              <InfoItem label="Telefon" value={employee?.phone} />
              <InfoItem
                label="Son Giriş"
                value={
                  account?.last_login_at
                    ? dayjs(account.last_login_at).format('DD.MM.YYYY HH:mm')
                    : '-'
                }
              />
            </SimpleGrid>
          </InfoCard>
        </Grid.Col>

        <Grid.Col span={{ base: 12, lg: 6 }}>
          <InfoCard title="Bağlı Olduğum Birim / Şantiye">
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
              <InfoItem
                label="Birim Tipi"
                value={
                  unit?.unit_type === 'OFFICE'
                    ? 'Merkez Ofis'
                    : unit?.unit_type === 'SITE'
                    ? 'Şantiye'
                    : '-'
                }
              />
              <InfoItem label="Departman" value={unit?.department} />
              <InfoItem label="Şantiye" value={unit?.site_name} />
            </SimpleGrid>
          </InfoCard>
        </Grid.Col>
 
        <Grid.Col span={{ base: 12, lg: 6 }}>
          <InfoCard title="Şifre ve Güvenlik">
            <PasswordInput
              label="Mevcut Şifre"
              radius="xl"
              leftSection={<IconKey size={16} />}
              value={passwordForm.current_password}
              onChange={(e) =>
                setPasswordForm({
                  ...passwordForm,
                  current_password: e.currentTarget.value,
                })
              }
            />

            <PasswordInput
              label="Yeni Şifre"
              radius="xl"
              leftSection={<IconShieldLock size={16} />}
              value={passwordForm.new_password}
              onChange={(e) =>
                setPasswordForm({
                  ...passwordForm,
                  new_password: e.currentTarget.value,
                })
              }
            />

            <PasswordInput
              label="Yeni Şifre Tekrar"
              radius="xl"
              leftSection={<IconShieldLock size={16} />}
              value={passwordForm.new_password_repeat}
              onChange={(e) =>
                setPasswordForm({
                  ...passwordForm,
                  new_password_repeat: e.currentTarget.value,
                })
              }
            />

            <Group justify="flex-end">
              <Button
                radius="xl"
                color="dark"
                leftSection={<IconKey size={16} />}
                loading={passwordSubmitting}
                onClick={handleChangePassword}
              >
                Şifreyi Güncelle
              </Button>
            </Group>
          </InfoCard>
        </Grid.Col>

        <Grid.Col span={12}>
          <ProfileLeaveCard
            hasEmployee={Boolean(employee?.employee_id)}
          />
        </Grid.Col>

      </Grid>
    </Stack>
  );
}
